import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getInstallerFileName,
  getResolvedKioskInstallerPath,
  isInstallerAvailable,
  isMobileDevice,
  kioskInstallerPaths,
  resolveInstallerUrl,
  triggerInstallerDownload,
} from "../../config/kioskInstall";
import { getKioskInstallInstructions } from "../../utils/kioskRuntime";
import "./kiosk-install-page.css";

const KioskInstallPage = () => {
  const navigate = useNavigate();
  const [availability, setAvailability] = useState<Record<string, boolean>>({});
  const [error, setError] = useState("");
  const isMobile = isMobileDevice();
  const mobileInstructions = getKioskInstallInstructions();

  const installers = useMemo(
    () => [
      { id: "mac", label: "macOS (.dmg)", path: kioskInstallerPaths.mac },
      { id: "win", label: "Windows (.exe)", path: kioskInstallerPaths.win },
    ],
    []
  );

  useEffect(() => {
    if (isMobile) {
      return;
    }

    let cancelled = false;

    const checkAll = async () => {
      const results: Record<string, boolean> = {};

      await Promise.all(
        installers.map(async (item) => {
          results[item.id] = await isInstallerAvailable(item.path);
        })
      );

      if (!cancelled) {
        setAvailability(results);
      }
    };

    checkAll();

    return () => {
      cancelled = true;
    };
  }, [installers, isMobile]);

  const handleDownload = async (path: string) => {
    setError("");

    if (isMobileDevice()) {
      setError("Desktop installer files are not supported on mobile. Add the app to your home screen instead.");
      return;
    }

    const available = await isInstallerAvailable(path);

    if (!available) {
      setError(
        `Installer file not found (${getInstallerFileName(path)}). Build it with npm run electron:build:mac or electron:build:win, then copy the file into public/downloads/.`
      );
      return;
    }

    triggerInstallerDownload(path);
  };

  const recommendedPath = getResolvedKioskInstallerPath();

  if (isMobile) {
    return (
      <div className="kiosk-install-page">
        <div className="kiosk-install-page-card">
          <h1>Install Mosida on Mobile</h1>
          <p>
            Mobile devices use the home screen app, not a desktop installer
            file.
          </p>
          <ol className="kiosk-install-page-steps">
            {mobileInstructions.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <button
            type="button"
            className="kiosk-install-page-back"
            onClick={() => navigate("/")}
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="kiosk-install-page">
      <div className="kiosk-install-page-card">
        <h1>Install Mosida Giftshop Kiosk</h1>
        <p>
          Download the desktop kiosk app for fullscreen mode on this device.
        </p>

        <button
          type="button"
          className="kiosk-install-page-primary"
          onClick={() => recommendedPath && handleDownload(recommendedPath)}
          disabled={!recommendedPath}
        >
          Download for this device
        </button>

        <div className="kiosk-install-page-list">
          {installers.map((item) => (
            <button
              key={item.id}
              type="button"
              className="kiosk-install-page-link"
              onClick={() => handleDownload(item.path)}
            >
              {item.label}
              {availability[item.id] === false && " (not uploaded yet)"}
            </button>
          ))}
        </div>

        {recommendedPath && (
          <p className="kiosk-install-page-note">
            Files are served from this app at{" "}
            <code>{resolveInstallerUrl(recommendedPath)}</code>
          </p>
        )}

        {error && (
          <p className="kiosk-install-page-error" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="kiosk-install-page-back"
          onClick={() => navigate("/")}
        >
          Back to home
        </button>
      </div>
    </div>
  );
};

export default KioskInstallPage;
